import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import AuthImage from "../components/AuthImage";
import { compressFileList, formatBytes } from "../lib/imageCompression";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Folder, FolderPlus, File as FileIcon, Upload, Trash, PencilSimple, Share, CaretRight,
  House, DownloadSimple, LinkSimple, Image as ImageIcon, VideoCamera, FileText, LockKey, Eye,
  X, CaretLeft, CaretRight as CaretRightArrow, CheckSquare, Square, FolderSimplePlus, Archive
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const previewPath = (f) => `/files/${f.id}/preview`;
const previewUrl = (f) => `${BASE}/api/files/${f.id}/preview`; // used for <iframe>/<video>

function isImage(f) { return (f?.mime_type || "").startsWith("image/"); }
function isPdf(f)   { return (f?.mime_type || "") === "application/pdf"; }
function isVideo(f) { return (f?.mime_type || "").startsWith("video/"); }

const KIND_LABELS = {
  client: "Client", property: "Property", job: "Job", project: "Project",
  emergency_job: "Emergency Job", job_request: "Job Request",
  before: "Before", after: "After", documents: "Documents", invoices: "Invoices",
  photos: "Photos", section: "Section", generic: "Folder",
};

function prettySize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fileIconFor(mime) {
  if (!mime) return FileIcon;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return VideoCamera;
  if (mime === "application/pdf" || mime.startsWith("text/")) return FileText;
  return FileIcon;
}

export default function FilesPage() {
  const { folderId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);  // { folder, breadcrumb, children, files } or roots list
  const [roots, setRoots] = useState([]);
  const [newFolder, setNewFolder] = useState({ open: false, name: "", kind: "generic", clientId: "" });
  const [renameDlg, setRenameDlg] = useState({ open: false, id: null, name: "", type: "folder" });
  const [shareDlg, setShareDlg] = useState(null);
  const [preview, setPreview] = useState(null); // {files: [], index: N}
  const [clients, setClients] = useState([]);
  // Bulk-selection state (Phase 288): a Set of file IDs the admin has ticked.
  // Selection is scoped to the current folder view and reset when navigating.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [moveDlg, setMoveDlg] = useState(null); // { fileIds: [...] } when open
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileInputRef = useRef(null);

  const loadRoot = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/folders");
      setRoots(data.folders || []);
      setCurrent(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load folders");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFolder = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/folders/${id}`);
      setCurrent(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Folder not found");
      navigate("/files");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (folderId) loadFolder(folderId);
    else loadRoot();
  }, [folderId, loadFolder, loadRoot]);

  // Load tenant clients once, for the "Link to Client" picker in the New Folder dialog
  useEffect(() => {
    let cancelled = false;
    API.get("/clients?limit=500")
      .then(({ data }) => {
        if (cancelled) return;
        setClients(data.clients || []);
      })
      .catch(() => { /* silently — non-admins may not have access; picker just won't show */ });
    return () => { cancelled = true; };
  }, []);

  const clientDisplayName = (c) => c.company_name || [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || c.email || "Client";

  const handleCreateFolder = async () => {
    if (!newFolder.name.trim()) return;
    try {
      const body = {
        name: newFolder.name.trim(),
        kind: newFolder.kind,
        parent_id: current?.folder?.id || null,
      };
      if (newFolder.clientId) {
        body.linked_entity_type = "client";
        body.linked_entity_id = newFolder.clientId;
      }
      await API.post("/folders", body);
      toast.success(newFolder.clientId ? "Folder created and linked to client" : "Folder created");
      setNewFolder({ open: false, name: "", kind: "generic", clientId: "" });
      if (current?.folder?.id) loadFolder(current.folder.id);
      else loadRoot();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create folder");
    }
  };

  const handleUpload = async (files) => {
    if (!current?.folder?.id || !files?.length) return;
    const prepToast = toast.loading("Preparing files…");
    const { files: prepared, saved } = await compressFileList(files);
    toast.dismiss(prepToast);
    if (saved > 200 * 1024) {
      toast.info(`Compressed images — saved ${formatBytes(saved)}`, { duration: 2500 });
    }
    const toastId = toast.loading(`Uploading ${prepared.length} file${prepared.length > 1 ? "s" : ""}…`);
    let ok = 0, fail = 0;
    for (const f of prepared) {
      const fd = new FormData();
      fd.append("folder_id", current.folder.id);
      fd.append("file", f);
      try {
        await API.post("/files/upload", fd);
        ok += 1;
      } catch (e) {
        fail += 1;
        const msg = formatApiError(e.response?.data?.detail) || "failed";
        toast.error(`${f.name}: ${msg}`);
      }
    }
    toast.dismiss(toastId);
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    if (fail) toast.error(`${fail} upload${fail > 1 ? "s" : ""} failed`);
    loadFolder(current.folder.id);
  };

  const handleDelete = async (type, id, name) => {
    const label = type === "folder" ? `folder "${name}" and all its contents` : `file "${name}"`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await API.delete(type === "folder" ? `/folders/${id}` : `/files/${id}`);
      toast.success(`${type === "folder" ? "Folder" : "File"} deleted`);
      if (type === "folder" && current?.folder?.id === id) {
        const parent = current.breadcrumb?.[current.breadcrumb.length - 2];
        if (parent) navigate(`/files/${parent.id}`);
        else navigate("/files");
      } else if (current?.folder?.id) {
        loadFolder(current.folder.id);
      } else {
        loadRoot();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const handleRename = async () => {
    try {
      if (renameDlg.type === "folder") {
        await API.patch(`/folders/${renameDlg.id}`, { name: renameDlg.name.trim() });
      } else {
        await API.patch(`/files/${renameDlg.id}`, { name: renameDlg.name.trim() });
      }
      toast.success("Renamed");
      setRenameDlg({ open: false, id: null, name: "", type: "folder" });
      if (current?.folder?.id) loadFolder(current.folder.id);
      else loadRoot();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rename failed");
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      // Blob response: server errors arrive as JSON-in-a-blob, so parse before toasting
      const res = await API.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_name || file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = "Download failed";
      try {
        const blob = e.response?.data;
        if (blob && blob.type?.includes("json")) {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          detail = formatApiError(parsed.detail) || detail;
        } else if (e.response?.status === 410) {
          detail = "File bytes are no longer on the server — please re-upload this file.";
        } else if (e.response?.status === 404) {
          detail = "File not found.";
        } else if (e.response?.status === 403) {
          detail = "You don't have access to this file.";
        }
      } catch { /* ignore parse errors */ }
      toast.error(detail);
    }
  };

  // ---------- Bulk actions (Phase 288) ----------
  // Reset the selection whenever the user navigates into a different folder —
  // selection is deliberately scoped to the current view so a stale set can
  // never target files the user can no longer see.
  useEffect(() => { setSelectedIds(new Set()); }, [folderId]);

  const toggleSelect = useCallback((fileId) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    // "All" here = every non-orphaned file currently on screen. Orphans have
    // no bytes to download and shouldn't be moved/deleted accidentally.
    const ids = (current?.files || []).filter((f) => !f.orphaned).map((f) => f.id);
    setSelectedIds(new Set(ids));
  }, [current]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Save a Blob returned by an axios request as a browser download.
  const _saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const _parseBlobErrorDetail = async (e, fallback) => {
    // Blob-response errors are trickier than plain JSON: axios delivers the
    // body as a Blob whose type may be JSON (backend HTTPException),
    // text/HTML (upstream ingress/timeout page), or missing (network hangup).
    // Try the JSON path first, then fall back to a status-aware hint so the
    // user sees "The server took too long…" instead of a generic message.
    try {
      const blob = e.response?.data;
      if (blob && blob.type?.includes("json")) {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        const detail = formatApiError(parsed.detail);
        if (detail) return detail;
      }
    } catch { /* ignore parse errors */ }
    const status = e.response?.status;
    if (status === 504 || status === 502 || status === 503) {
      return "The server took too long to prepare the ZIP. Try downloading fewer files at a time.";
    }
    if (status === 413) {
      return "Selection is too large — the ZIP would exceed the 2 GB limit. Please narrow your selection.";
    }
    if (status === 401 || status === 403) {
      return "You don't have permission to download some of these files.";
    }
    if (!e.response) {
      // No response at all — network dropped, likely a proxy timeout.
      return "The download was interrupted. This usually means the server took too long — try fewer files at a time.";
    }
    return fallback;
  };

  const handleBulkDownload = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    const toastId = toast.loading(`Zipping ${ids.length} file${ids.length > 1 ? "s" : ""}…`);
    try {
      const res = await API.post("/files/bulk-download", { file_ids: ids }, { responseType: "blob" });
      const folderName = current?.folder?.name || "files-hub";
      const stamp = new Date().toISOString().slice(0, 10);
      const safeFolder = folderName.replace(/[^A-Za-z0-9._\- ]+/g, "_").trim();
      _saveBlob(res.data, `${safeFolder}-selection-${stamp}.zip`);
      toast.dismiss(toastId);
      // Streaming ZIP can't know delivered count at header time — X-Files-Included
      // reflects ATTEMPTED files, and any mid-stream storage timeout silently
      // drops files from the archive. Keep the toast honest by not claiming
      // an exact number; the user can inspect the extracted ZIP if they want
      // a precise count.
      toast.success("Download ready — check your downloads folder.");
      clearSelection();
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(await _parseBlobErrorDetail(e, "Bulk download failed"));
    } finally { setBulkBusy(false); }
  };

  const handleDownloadFolderZip = async () => {
    if (!current?.folder?.id) return;
    const folderName = current.folder.name || "folder";
    setBulkBusy(true);
    const toastId = toast.loading(`Zipping "${folderName}"…`);
    try {
      const res = await API.get(`/folders/${current.folder.id}/download-zip`, { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 10);
      const safeFolder = folderName.replace(/[^A-Za-z0-9._\- ]+/g, "_").trim();
      _saveBlob(res.data, `${safeFolder}-${stamp}.zip`);
      toast.dismiss(toastId);
      toast.success("Download ready — check your downloads folder.");
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(await _parseBlobErrorDetail(e, "Folder ZIP download failed"));
    } finally { setBulkBusy(false); }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(
      `Delete ${ids.length} file${ids.length > 1 ? "s" : ""}? This cannot be undone.`
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await API.post("/files/bulk-delete", { file_ids: ids });
      toast.success(`Deleted ${data.deleted || 0} file${(data.deleted || 0) === 1 ? "" : "s"}`);
      clearSelection();
      if (current?.folder?.id) loadFolder(current.folder.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  };

  const handleBulkMove = async (targetFolderId) => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !targetFolderId) return;
    setBulkBusy(true);
    try {
      const { data } = await API.post("/files/bulk-move", {
        file_ids: ids, target_folder_id: targetFolderId,
      });
      toast.success(`Moved ${data.moved || 0} file${(data.moved || 0) === 1 ? "" : "s"} to "${data.target_folder_name}"`);
      clearSelection();
      setMoveDlg(null);
      if (current?.folder?.id) loadFolder(current.folder.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk move failed");
    } finally { setBulkBusy(false); }
  };

  // ---------- Render ----------
  return (
    <>
      <div className="p-6 space-y-6" data-testid="files-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">File Hub</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Central storage for client, property, job and project documents & media.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {current?.folder && (
              <>
                <input ref={fileInputRef} type="file" multiple hidden
                       onChange={(e) => handleUpload(Array.from(e.target.files || []))}
                       data-testid="files-upload-input" />
                <Button onClick={() => fileInputRef.current?.click()} data-testid="files-upload-btn">
                  <Upload className="h-4 w-4 mr-2" /> Upload
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadFolderZip}
                  disabled={bulkBusy}
                  title="Recursively bundle this folder + all subfolders into a ZIP"
                  data-testid="files-download-folder-zip-btn"
                >
                  <Archive className="h-4 w-4 mr-2" /> Download all as ZIP
                </Button>
              </>
            )}
            <Button variant="outline"
                    onClick={() => setNewFolder({ open: true, name: "", kind: current?.folder ? "generic" : "client", clientId: "" })}
                    data-testid="files-new-folder-btn">
              <FolderPlus className="h-4 w-4 mr-2" /> New Folder
            </Button>
          </div>
        </div>

        {/* Breadcrumb */}
        <nav className="flex items-center flex-wrap gap-1 text-sm text-slate-600 dark:text-slate-400">
          <Link to="/files" className="inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100" data-testid="files-breadcrumb-root">
            <House className="h-4 w-4" /> File Hub
          </Link>
          {current?.breadcrumb?.map((c, i) => (
            <span key={c.id} className="inline-flex items-center gap-1">
              <CaretRight className="h-3.5 w-3.5 text-slate-400" />
              {i === current.breadcrumb.length - 1 ? (
                <span className="font-semibold text-slate-900 dark:text-slate-100">{c.name}</span>
              ) : (
                <Link to={`/files/${c.id}`} className="hover:text-slate-900 dark:hover:text-slate-100">{c.name}</Link>
              )}
            </span>
          ))}
        </nav>

        {/* Content area */}
        {loading ? (
          <div className="py-20 text-center text-slate-400 text-sm">Loading…</div>
        ) : !current ? (
          <RootsGrid
            roots={roots}
            onOpen={(id) => navigate(`/files/${id}`)}
            onNew={() => setNewFolder({ open: true, name: "", kind: "client" })}
          />
        ) : (
          <FolderContents
            data={current}
            onOpenFolder={(id) => navigate(`/files/${id}`)}
            onRenameFolder={(f) => setRenameDlg({ open: true, id: f.id, name: f.name, type: "folder" })}
            onDeleteFolder={(f) => handleDelete("folder", f.id, f.name)}
            onShareFolder={(f) => setShareDlg({ kind: "folder", id: f.id, name: f.name })}
            onRenameFile={(f) => setRenameDlg({ open: true, id: f.id, name: f.name, type: "file" })}
            onDeleteFile={(f) => handleDelete("file", f.id, f.name)}
            onDownloadFile={handleDownloadFile}
            onShareFile={(f) => setShareDlg({ kind: "file", id: f.id, name: f.name })}
            onPreviewFile={(f) => {
              const previewables = (current?.files || []).filter((x) => isImage(x) || isPdf(x) || isVideo(x));
              const idx = previewables.findIndex((x) => x.id === f.id);
              setPreview({ files: previewables.length ? previewables : [f], index: Math.max(0, idx) });
            }}
            onDrop={handleUpload}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectAll={selectAllVisible}
            onClearSelection={clearSelection}
            onBulkDownload={handleBulkDownload}
            onBulkDelete={handleBulkDelete}
            onBulkMove={() => setMoveDlg({ fileIds: Array.from(selectedIds) })}
            bulkBusy={bulkBusy}
          />
        )}
      </div>

      {/* New folder dialog */}
      <Dialog open={newFolder.open} onOpenChange={(o) => setNewFolder({ ...newFolder, open: o })}>
        <DialogContent data-testid="files-new-folder-dialog">
          <DialogHeader>
            <DialogTitle>Create folder {current?.folder ? `inside "${current.folder.name}"` : "at top level"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name</Label>
              <Input value={newFolder.name}
                     onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                     placeholder="e.g. Acme Holdings, 42 Harrow Road, Job #1043"
                     data-testid="files-new-folder-name"
                     autoFocus />
            </div>
            <div>
              <Label>Folder type</Label>
              <Select value={newFolder.kind} onValueChange={(v) => setNewFolder({ ...newFolder, kind: v })}>
                <SelectTrigger data-testid="files-new-folder-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!current?.folder && <SelectItem value="client">Client</SelectItem>}
                  <SelectItem value="property">Property (auto-creates Before / After / Documents / Invoices)</SelectItem>
                  <SelectItem value="job">Job (auto-creates Before / After / Documents)</SelectItem>
                  <SelectItem value="project">Project (auto-creates Before / After / Documents / Invoices)</SelectItem>
                  <SelectItem value="emergency_job">Emergency Job (auto-creates Before / After / Documents)</SelectItem>
                  <SelectItem value="generic">Generic folder (no template)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!!clients.length && (
              <div>
                <Label>Link to client <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
                <Select value={newFolder.clientId || "__none__"}
                        onValueChange={(v) => {
                          const nextId = v === "__none__" ? "" : v;
                          const picked = clients.find((c) => c.id === nextId);
                          // If user picks a client, helpfully pre-fill the folder name (only when blank)
                          // and nudge the kind to "client" when creating at root.
                          setNewFolder((s) => ({
                            ...s,
                            clientId: nextId,
                            name: s.name || (picked ? clientDisplayName(picked) : ""),
                            kind: nextId && !current?.folder && s.kind === "generic" ? "client" : s.kind,
                          }));
                        }}>
                  <SelectTrigger data-testid="files-new-folder-client"><SelectValue placeholder="None — not linked" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None — not linked to a client</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{clientDisplayName(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newFolder.clientId && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    This folder will appear on the client's record and stays scoped to them.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolder({ ...newFolder, open: false })}>Cancel</Button>
            <Button onClick={handleCreateFolder} data-testid="files-new-folder-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameDlg.open} onOpenChange={(o) => setRenameDlg({ ...renameDlg, open: o })}>
        <DialogContent data-testid="files-rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename {renameDlg.type}</DialogTitle>
          </DialogHeader>
          <Input value={renameDlg.name} onChange={(e) => setRenameDlg({ ...renameDlg, name: e.target.value })} autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDlg({ ...renameDlg, open: false })}>Cancel</Button>
            <Button onClick={handleRename}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      {shareDlg && <ShareDialog target={shareDlg} onClose={() => setShareDlg(null)} />}

      {/* Bulk-move destination picker */}
      {moveDlg && (
        <MoveFilesDialog
          fileIds={moveDlg.fileIds}
          currentFolderId={current?.folder?.id}
          onClose={() => setMoveDlg(null)}
          onMove={handleBulkMove}
          busy={bulkBusy}
        />
      )}

      {preview && (
        <Lightbox
          files={preview.files}
          index={preview.index}
          onClose={() => setPreview(null)}
          onIndex={(i) => setPreview((p) => ({ ...p, index: i }))}
          onDownload={handleDownloadFile}
        />
      )}
    </>
  );
}

function RootsGrid({ roots, onOpen, onNew }) {
  if (!roots.length) {
    return (
      <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-16 text-center" data-testid="files-empty-state">
        <Folder className="h-12 w-12 mx-auto text-slate-400" />
        <p className="mt-4 text-slate-600 dark:text-slate-300 font-semibold">No folders yet</p>
        <p className="text-sm text-slate-500 mt-1">Start by creating a folder for your first client.</p>
        <Button onClick={onNew} className="mt-4" data-testid="files-empty-create-btn">
          <FolderPlus className="h-4 w-4 mr-2" /> Create first folder
        </Button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="files-roots-grid">
      {roots.map((f) => (
        <button key={f.id} onClick={() => onOpen(f.id)}
                className="group text-left p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400 hover:shadow-sm transition"
                data-testid={`files-root-${f.id}`}>
          <div className="flex items-start gap-3">
            <Folder className="h-8 w-8 text-blue-500 shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">{f.name}</p>
              <p className="text-xs text-slate-500 mt-1">{KIND_LABELS[f.kind] || f.kind}</p>
              {f.linked_entity_type === "client" && (
                <span className="inline-flex items-center gap-1 mt-2 px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-[10px] font-medium">
                  <LinkSimple className="h-2.5 w-2.5" /> {f.linked_entity_name || "Client linked"}
                </span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function FolderContents({ data, onOpenFolder, onRenameFolder, onDeleteFolder, onShareFolder,
                          onRenameFile, onDeleteFile, onDownloadFile, onShareFile, onPreviewFile, onDrop,
                          selectedIds, onToggleSelect, onSelectAll, onClearSelection,
                          onBulkDownload, onBulkDelete, onBulkMove, bulkBusy }) {
  const [dragOver, setDragOver] = useState(false);
  const folderEmpty = !data.children?.length && !data.files?.length;
  const selectedCount = selectedIds?.size || 0;
  const eligibleCount = (data.files || []).filter((f) => !f.orphaned).length;
  const allSelected = eligibleCount > 0 && selectedCount === eligibleCount;
  const isSelected = (id) => selectedIds?.has(id);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length) onDrop(files);
      }}
      className={`rounded-lg border-2 border-dashed transition ${dragOver ? "border-blue-500 bg-blue-50/40 dark:bg-blue-950/20" : "border-transparent"}`}
      data-testid="files-folder-contents"
    >
      {/* Bulk-selection toolbar — shown whenever the folder has selectable files */}
      {!!eligibleCount && (
        <div
          className={`mb-4 flex flex-wrap items-center justify-between gap-3 px-3 py-2 rounded-md border transition ${
            selectedCount > 0
              ? "border-primary/60 bg-primary/5 dark:bg-primary/10"
              : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40"
          }`}
          data-testid="files-bulk-toolbar"
        >
          <div className="flex items-center gap-3 text-sm">
            <button
              type="button"
              onClick={allSelected ? onClearSelection : onSelectAll}
              className="inline-flex items-center gap-1.5 font-medium text-slate-700 dark:text-slate-200 hover:text-primary transition"
              data-testid="files-bulk-toggle-all"
              title={allSelected ? "Clear selection" : "Select all visible files"}
            >
              {allSelected ? <CheckSquare className="h-4 w-4" weight="fill" /> : <Square className="h-4 w-4" />}
              <span>{allSelected ? "Clear selection" : "Select all"}</span>
            </button>
            <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="files-bulk-count">
              {selectedCount > 0
                ? `${selectedCount} of ${eligibleCount} selected`
                : `${eligibleCount} file${eligibleCount === 1 ? "" : "s"} available`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={onBulkDownload}
              data-testid="files-bulk-download-btn"
            >
              <DownloadSimple className="h-4 w-4 mr-1.5" />
              Download {selectedCount > 0 ? `(${selectedCount})` : ""}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={onBulkMove}
              data-testid="files-bulk-move-btn"
            >
              <FolderSimplePlus className="h-4 w-4 mr-1.5" />
              Move {selectedCount > 0 ? `(${selectedCount})` : ""}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={onBulkDelete}
              className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-300 dark:border-red-900/60 dark:hover:bg-red-950/30"
              data-testid="files-bulk-delete-btn"
            >
              <Trash className="h-4 w-4 mr-1.5" />
              Delete {selectedCount > 0 ? `(${selectedCount})` : ""}
            </Button>
          </div>
        </div>
      )}

      {/* Subfolders */}
      {!!data.children?.length && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Folders</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {data.children.map((f) => (
              <div key={f.id}
                   className="group p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400 transition"
                   data-testid={`files-folder-${f.id}`}>
                <button onClick={() => onOpenFolder(f.id)} className="flex items-center gap-2 w-full text-left">
                  <Folder className="h-6 w-6 text-blue-500 shrink-0" />
                  <span className="font-medium text-slate-900 dark:text-slate-100 truncate">{f.name}</span>
                </button>
                {f.linked_entity_type === "client" && (
                  <span className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-[10px] font-medium">
                    <LinkSimple className="h-2.5 w-2.5" /> {f.linked_entity_name || "Client linked"}
                  </span>
                )}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">{KIND_LABELS[f.kind] || ""}</span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    <IconBtn title="Share" onClick={() => onShareFolder(f)} testid={`files-folder-share-${f.id}`}><Share className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn title="Rename" onClick={() => onRenameFolder(f)} testid={`files-folder-rename-${f.id}`}><PencilSimple className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn title="Delete" onClick={() => onDeleteFolder(f)} testid={`files-folder-delete-${f.id}`}><Trash className="h-3.5 w-3.5" /></IconBtn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Files */}
      {!!data.files?.length && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Files</p>
            <p className="text-[10px] text-slate-400">Click an image or document to preview · download button saves it locally</p>
          </div>

          {/* Image thumbnails grid */}
          {data.files.some((f) => isImage(f) && !f.orphaned) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-3" data-testid="files-image-grid">
              {data.files.filter((f) => isImage(f) && !f.orphaned).map((f) => (
                <div key={f.id}
                     className={`group relative aspect-square rounded-md overflow-hidden border transition ${
                       isSelected(f.id)
                         ? "border-primary ring-2 ring-primary shadow-sm"
                         : "border-slate-200 dark:border-slate-700 hover:ring-2 hover:ring-primary"
                     } bg-slate-50 dark:bg-slate-900`}
                     data-testid={`files-thumb-wrapper-${f.id}`}>
                  {/* Selection checkbox — top-left, always visible on selected items,
                      appears on hover otherwise so the grid stays clean. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggleSelect?.(f.id); }}
                    className={`absolute top-1.5 left-1.5 z-10 h-6 w-6 rounded-md flex items-center justify-center transition ${
                      isSelected(f.id)
                        ? "bg-primary text-primary-foreground opacity-100"
                        : "bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-300 opacity-0 group-hover:opacity-100"
                    } shadow-sm`}
                    title={isSelected(f.id) ? "Deselect" : "Select"}
                    data-testid={`files-thumb-select-${f.id}`}
                  >
                    {isSelected(f.id)
                      ? <CheckSquare className="h-4 w-4" weight="fill" />
                      : <Square className="h-4 w-4" />}
                  </button>
                  <button onClick={() => onPreviewFile?.(f)}
                          className="absolute inset-0 h-full w-full"
                          data-testid={`files-thumb-${f.id}`}
                          title={`Preview ${f.name}`}>
                    <AuthImage path={previewPath(f)} alt={f.name}
                               className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] truncate opacity-0 group-hover:opacity-100 transition-opacity">
                      {f.name}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1">
            {data.files.map((f) => {
              const Icon = fileIconFor(f.mime_type);
              const previewable = !f.orphaned && (isImage(f) || isPdf(f) || isVideo(f));
              const selectable = !f.orphaned;
              return (
                <div key={f.id}
                     className={`flex items-center gap-3 p-2.5 rounded-md border bg-white dark:bg-slate-900 transition ${
                       isSelected(f.id)
                         ? "border-primary ring-1 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                         : f.orphaned
                           ? "border-amber-300 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20"
                           : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                     }`}
                     data-testid={`files-file-${f.id}`}>
                  {/* Bulk-selection checkbox */}
                  {selectable ? (
                    <button
                      type="button"
                      onClick={() => onToggleSelect?.(f.id)}
                      className="h-5 w-5 shrink-0 flex items-center justify-center text-slate-500 hover:text-primary transition"
                      title={isSelected(f.id) ? "Deselect" : "Select"}
                      data-testid={`files-file-select-${f.id}`}
                    >
                      {isSelected(f.id)
                        ? <CheckSquare className="h-4 w-4 text-primary" weight="fill" />
                        : <Square className="h-4 w-4" />}
                    </button>
                  ) : (
                    <span className="h-5 w-5 shrink-0" aria-hidden />
                  )}
                  {isImage(f) && !f.orphaned ? (
                    <button onClick={() => onPreviewFile?.(f)} className="h-10 w-10 rounded overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0" title="Preview">
                      <AuthImage path={previewPath(f)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ) : (
                    <Icon className={`h-5 w-5 shrink-0 ${f.orphaned ? "text-amber-600" : "text-slate-500"}`} />
                  )}
                  <button onClick={previewable ? () => onPreviewFile?.(f) : undefined}
                          disabled={!previewable}
                          className={`flex-1 min-w-0 text-left ${previewable ? "" : "cursor-default"}`}
                          data-testid={`files-file-open-${f.id}`}>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{f.name}</p>
                      {f.orphaned && (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 px-1.5 py-0.5 rounded"
                              title="This file was uploaded before our storage was upgraded. The bytes are no longer available — please delete this placeholder and re-upload the file."
                              data-testid={`files-file-orphaned-${f.id}`}>
                          Re-upload needed
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {prettySize(f.size)} · {f.uploaded_by_name || "Admin"} · {new Date(f.uploaded_at).toLocaleString()}
                    </p>
                  </button>
                  <div className="flex items-center gap-1">
                    {previewable && (
                      <IconBtn title="Preview" onClick={() => onPreviewFile?.(f)} testid={`files-file-preview-${f.id}`}><Eye className="h-4 w-4" /></IconBtn>
                    )}
                    {!f.orphaned && (
                      <IconBtn title="Download" onClick={() => onDownloadFile(f)} testid={`files-file-download-${f.id}`}><DownloadSimple className="h-4 w-4" /></IconBtn>
                    )}
                    <IconBtn title="Share" onClick={() => onShareFile(f)} testid={`files-file-share-${f.id}`}><Share className="h-4 w-4" /></IconBtn>
                    <IconBtn title="Rename" onClick={() => onRenameFile(f)} testid={`files-file-rename-${f.id}`}><PencilSimple className="h-4 w-4" /></IconBtn>
                    <IconBtn title="Delete" onClick={() => onDeleteFile(f)} testid={`files-file-delete-${f.id}`}><Trash className="h-4 w-4" /></IconBtn>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {folderEmpty && (
        <div className="py-16 text-center text-slate-500" data-testid="files-folder-empty">
          <Upload className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600" />
          <p className="mt-3 text-sm">Drag & drop files here, or use the Upload button</p>
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
            className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300">
      {children}
    </button>
  );
}

function ShareDialog({ target, onClose }) {
  const [form, setForm] = useState({ password: "", expires_days: "never", allow_download: true });
  const [saving, setSaving] = useState(false);
  const [shares, setShares] = useState([]);
  const [result, setResult] = useState(null);

  const loadShares = useCallback(async () => {
    try {
      const qs = target.kind === "folder" ? `folder_id=${target.id}` : `file_id=${target.id}`;
      const { data } = await API.get(`/shares?${qs}`);
      setShares(data.shares || []);
    } catch {
      setShares([]);
    }
  }, [target]);

  useEffect(() => { loadShares(); }, [loadShares]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        [target.kind === "folder" ? "folder_id" : "file_id"]: target.id,
        password: form.password.trim() || null,
        allow_download: form.allow_download,
      };
      if (form.expires_days !== "never") {
        const d = new Date();
        d.setDate(d.getDate() + parseInt(form.expires_days, 10));
        body.expires_at = d.toISOString();
      }
      const { data } = await API.post("/shares", body);
      setResult(data);
      await loadShares();
      toast.success("Share link created");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create share");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share link? Recipients will lose access immediately.")) return;
    try {
      await API.delete(`/shares/${id}`);
      toast.success("Link revoked");
      loadShares();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Revoke failed");
    }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const shareUrl = (token) => `${window.location.origin}/share/${token}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="files-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share className="h-5 w-5" /> Share "{target.name}"</DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4 py-2">
            <div>
              <Label className="flex items-center gap-1"><LockKey className="h-3.5 w-3.5" /> Password (optional)</Label>
              <Input type="text" value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     placeholder="Leave blank for no password"
                     data-testid="files-share-password" />
            </div>
            <div>
              <Label>Expires after</Label>
              <Select value={form.expires_days} onValueChange={(v) => setForm({ ...form, expires_days: v })}>
                <SelectTrigger data-testid="files-share-expiry"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.allow_download}
                     onChange={(e) => setForm({ ...form, allow_download: e.target.checked })}
                     data-testid="files-share-allow-download" />
              Allow download (uncheck for view-only)
            </label>
          </div>
        )}

        {result && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">Share link created. Copy and send it:</p>
            <div className="flex gap-2">
              <Input readOnly value={shareUrl(result.token)} data-testid="files-share-url" />
              <Button onClick={() => copy(shareUrl(result.token))} data-testid="files-share-copy-btn">
                <LinkSimple className="h-4 w-4 mr-1" /> Copy
              </Button>
            </div>
            <div className="text-xs text-slate-500">
              {result.has_password ? "Password required" : "No password"} ·
              {result.expires_at ? ` expires ${new Date(result.expires_at).toLocaleString()}` : " never expires"} ·
              {result.allow_download ? " download allowed" : " view-only"}
            </div>
          </div>
        )}

        {!!shares.length && (
          <div className="mt-2 border-t pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Existing links</p>
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {shares.map((s) => (
                <ShareRow key={s.id} share={s} shareUrl={shareUrl} onCopy={copy} onRevoke={revoke} />
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="files-share-create-btn">
                {saving ? "Creating…" : "Create Link"}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareRow({ share: s, shareUrl, onCopy, onRevoke }) {
  const [showLog, setShowLog] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);

  const loadLog = useCallback(async () => {
    setLoadingLog(true);
    try {
      const { data } = await API.get(`/shares/${s.id}/access-log`);
      setEvents(data.events || []);
    } catch {
      setEvents([]);
    } finally {
      setLoadingLog(false);
    }
  }, [s.id]);

  const toggleLog = () => {
    const next = !showLog;
    setShowLog(next);
    if (next && !events.length) loadLog();
  };

  return (
    <div className="rounded bg-slate-50 dark:bg-slate-800/40 overflow-hidden">
      <div className="flex items-center justify-between gap-2 text-xs p-2">
        <div className="min-w-0 flex-1">
          <button onClick={() => onCopy(shareUrl(s.token))} className="truncate block text-blue-600 hover:underline" data-testid={`files-share-existing-${s.id}`}>
            {shareUrl(s.token)}
          </button>
          <span className="text-slate-400">
            {s.has_password ? "Password" : "Open"} · {s.expires_at ? `expires ${new Date(s.expires_at).toLocaleDateString()}` : "never"} · {s.access_count} open{s.access_count === 1 ? "" : "s"}
            {s.last_accessed_at && ` · last ${new Date(s.last_accessed_at).toLocaleDateString()}`}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={toggleLog} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600" title="View access log" data-testid={`files-share-log-btn-${s.id}`}>
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onRevoke(s.id)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-red-600" title="Revoke" data-testid={`files-share-revoke-${s.id}`}>
            <Trash className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {showLog && (
        <div className="px-3 pb-2 border-t border-slate-200 dark:border-slate-700" data-testid={`files-share-log-${s.id}`}>
          {loadingLog ? (
            <p className="text-[11px] text-slate-400 py-2">Loading access log…</p>
          ) : !events.length ? (
            <p className="text-[11px] text-slate-400 py-2">No-one has opened this link yet.</p>
          ) : (
            <ul className="space-y-1 py-2 max-h-40 overflow-y-auto">
              {events.map((ev) => (
                <li key={ev.id} className="flex items-center justify-between gap-2 text-[11px] text-slate-600 dark:text-slate-300">
                  <span className="truncate">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 uppercase text-[9px] font-semibold mr-2">
                      {ev.event}
                    </span>
                    {ev.file_name ? <span className="font-medium">{ev.file_name}</span> : null}
                    {ev.ip ? <span className="text-slate-400"> · {ev.ip}</span> : null}
                  </span>
                  <span className="text-slate-400 shrink-0">{new Date(ev.at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Lightbox({ files, index, onClose, onIndex, onDownload }) {
  const current = files[index];
  const canPrev = index > 0;
  const canNext = index < files.length - 1;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft" && canPrev) onIndex?.(index - 1);
      else if (e.key === "ArrowRight" && canNext) onIndex?.(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, canPrev, canNext, onClose, onIndex]);

  if (!current) return null;
  const url = previewUrl(current);

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur flex flex-col" data-testid="files-lightbox" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate text-sm">{current.name}</p>
          <p className="text-[11px] text-white/60 truncate">{prettySize(current.size)} · {current.uploaded_by_name || "Admin"} · {new Date(current.uploaded_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => onDownload?.(current)} title="Download"
                  className="h-9 w-9 flex items-center justify-center rounded hover:bg-white/10"
                  data-testid="files-lightbox-download"><DownloadSimple className="h-4 w-4" /></button>
          <button onClick={onClose} title="Close (Esc)"
                  className="h-9 w-9 flex items-center justify-center rounded hover:bg-white/10"
                  data-testid="files-lightbox-close"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="relative flex-1 flex items-center justify-center p-4 min-h-0">
        {canPrev && (
          <button onClick={() => onIndex?.(index - 1)} aria-label="Previous"
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                  data-testid="files-lightbox-prev"><CaretLeft className="h-5 w-5" /></button>
        )}
        {canNext && (
          <button onClick={() => onIndex?.(index + 1)} aria-label="Next"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                  data-testid="files-lightbox-next"><CaretRightArrow className="h-5 w-5" /></button>
        )}

        {isImage(current) && (
          <AuthImage path={previewPath(current)} alt={current.name}
                     className="max-h-full max-w-full object-contain rounded-md shadow-2xl" />
        )}
        {isPdf(current) && (
          <iframe src={url} title={current.name} className="w-full h-full rounded-md bg-white" />
        )}
        {isVideo(current) && (
          <video src={url} controls autoPlay className="max-h-full max-w-full rounded-md" />
        )}
      </div>

      {files.length > 1 && (
        <div className="text-center text-white/60 text-[11px] pb-3 pt-1">{index + 1} of {files.length}</div>
      )}
    </div>
  );
}



/**
 * Bulk-move destination picker.
 *
 * Loads the tenant's folder tree once, flattens it into an indented picker,
 * and posts the selection through the parent's `onMove` handler. The current
 * folder is hidden from the list so we don't offer a no-op destination.
 */
function MoveFilesDialog({ fileIds, currentFolderId, onClose, onMove, busy }) {
  const [tree, setTree] = useState([]);           // [{ id, name, depth, kind }, ...]
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Load all root folders, then walk each one via /folders/{id} to get children.
        // We keep this lightweight: only walk 2 levels deep so the picker stays snappy
        // (the vast majority of file structures are Client → Property/Job → subfolder).
        const { data: rootData } = await API.get("/folders");
        const roots = rootData.folders || [];
        const flat = [];
        for (const r of roots) {
          flat.push({ id: r.id, name: r.name, depth: 0, kind: r.kind });
          try {
            const { data: fd } = await API.get(`/folders/${r.id}`);
            for (const c of (fd.children || [])) {
              flat.push({ id: c.id, name: c.name, depth: 1, kind: c.kind });
              try {
                const { data: sd } = await API.get(`/folders/${c.id}`);
                for (const gc of (sd.children || [])) {
                  flat.push({ id: gc.id, name: gc.name, depth: 2, kind: gc.kind });
                }
              } catch { /* skip inaccessible subfolders */ }
            }
          } catch { /* skip inaccessible root subfolders */ }
        }
        if (!cancelled) setTree(flat);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || "Failed to load folder list");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return tree
      .filter((f) => f.id !== currentFolderId)
      .filter((f) => !q || f.name.toLowerCase().includes(q));
  }, [tree, filter, currentFolderId]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="files-move-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderSimplePlus className="h-5 w-5" />
            Move {fileIds.length} file{fileIds.length === 1 ? "" : "s"} to…
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Input
            autoFocus
            placeholder="Search folders…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="files-move-search"
          />
          <div className="max-h-72 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md">
            {loading ? (
              <p className="p-4 text-xs text-slate-500 text-center">Loading folders…</p>
            ) : !filtered.length ? (
              <p className="p-4 text-xs text-slate-500 text-center">No folders match.</p>
            ) : (
              filtered.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setTarget(f)}
                  className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 border-slate-100 dark:border-slate-800 flex items-center gap-2 transition ${
                    target?.id === f.id
                      ? "bg-primary/10 dark:bg-primary/20 text-primary font-semibold"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                  style={{ paddingLeft: `${12 + f.depth * 20}px` }}
                  data-testid={`files-move-pick-${f.id}`}
                >
                  <Folder className="h-4 w-4 shrink-0 text-blue-500" />
                  <span className="truncate">{f.name}</span>
                  {f.kind && <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-400">{f.kind}</span>}
                </button>
              ))
            )}
          </div>
          {target && (
            <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="files-move-selected">
              Selected destination: <span className="font-semibold text-slate-800 dark:text-slate-100">{target.name}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={() => target && onMove(target.id)}
            disabled={!target || busy}
            data-testid="files-move-confirm-btn"
          >
            {busy ? "Moving…" : "Move here"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
